import React from 'react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';

type Theme = 'light' | 'dark';

interface TooltipProps {
  child: ReactNode;
  text: string;
  theme?: Theme;
  maxWidth?: number;
  className?: string;
}

type TooltipStyle = {
  top: number;
  left: number;
  arrowLeft: number;
};

export function Tooltip({
  child,
  text,
  theme = 'dark',
  maxWidth = 300,
  className = ''
}: TooltipProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<TooltipStyle | null>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const pad = 8;
    const vw = window.innerWidth;

    let left = r.left;
    const top = r.bottom + pad;

    // Не выходим за края экрана
    if (left + maxWidth > vw - pad) left = vw - maxWidth - pad;
    if (left < pad) left = pad;

    // Центр элемента и позиция стрелки относительно тултипа
    const elementCenterX = r.left + r.width / 2;
    const rawArrowLeft = elementCenterX - left;

    //  чтобы стрелка не уезжала за границы
    const arrowLeft = Math.min(
      Math.max(Math.round(rawArrowLeft), pad),
      maxWidth - pad,
    );

    setStyle({ top, left, arrowLeft });
  };

  const hide = () => setStyle(null);

  useEffect(() => {
    if (!style) return;

    const tooltipHide = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) hide();
    };

    window.addEventListener('resize', hide);
    window.addEventListener('scroll', hide);
    document.addEventListener('mousemove', tooltipHide);
    return () => {
      window.removeEventListener('resize', hide);
      window.removeEventListener('scroll', hide);
      document.removeEventListener('mousemove', tooltipHide);
    };
  }, [style]);

  return (
    <>
      <div
        className={className}
        ref={ref}
        onMouseEnter={show}
        onFocus={show}
        onBlur={hide}
      >
        {child}
      </div>

      {style &&
        createPortal(
          <div
            className={`tooltip tooltip--${theme}`}
            style={{
              position: 'fixed',
              top: style.top,
              left: style.left,
              maxWidth,
            }}
          >
            <div
              className="tooltip__arrow"
              style={{ left: `${style.arrowLeft}px` }}
            ></div>
            <p className="tooltip__text" lang="ru">
              {text}
            </p>
          </div>,
          document.body,
        )}
    </>
  );
}
